/**
 * FarmCropsChart component for displaying crop distribution.
 * Extracted from FarmDetailsModal.tsx.
 */

import React from "react";
import { Paper, Typography, Box } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import { Chip } from "@mui/material";

interface CropData {
  id: string;
  value: number;
  label: string;
  color: string;
}

interface FarmCrop {
  id: string;
  name: string;
  area: number;
  isMain: boolean;
  description?: string;
}

interface FarmCropsChartProps {
  farmCrops: FarmCrop[];
}

export const FarmCropsChart: React.FC<FarmCropsChartProps> = React.memo(({ farmCrops }) => {
  const cropData: CropData[] =
    farmCrops?.map((crop) => ({
      id: crop.id,
      value: crop.area,
      label: crop.name,
      color: crop.isMain ? "#2196f3" : "#90caf9",
    })) || [];

  const totalArea = cropData.reduce((sum, crop) => sum + crop.value, 0);

  if (farmCrops.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <AgricultureIcon /> Distribución de Cultivos
      </Typography>
      <Box height={250}>
        <PieChart
          series={[
            {
              data: cropData,
              innerRadius: 30,
              outerRadius: 80,
              paddingAngle: 2,
              cornerRadius: 4,
              startAngle: -90,
              endAngle: 270,
            },
          ]}
          slotProps={{
            legend: {
              direction: "row",
              position: {
                vertical: "bottom",
                horizontal: "middle",
              },
              padding: 8,
              itemMarkWidth: 8,
              itemMarkHeight: 8,
              markGap: 5,
              itemGap: 12,
            },
          }}
          height={250}
          margin={{ top: 10, bottom: 40, left: 20, right: 20 }}
        />
      </Box>
      <Box mt={2}>
        <Typography
          variant="subtitle2"
          align="center"
          gutterBottom
        >
          Área Total: {totalArea.toFixed(2)} Ha
        </Typography>
        {farmCrops.map((crop) => (
          <Box key={crop.id} mb={1}>
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                {crop.name}
                {crop.isMain && (
                  <Chip
                    label="Principal"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </span>
              <span>
                {crop.area.toFixed(2)} Ha (
                {((crop.area / totalArea) * 100).toFixed(1)}%)
              </span>
            </Typography>
            {crop.description && (
              <Typography variant="caption" color="textSecondary">
                {crop.description}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
});
FarmCropsChart.displayName = "FarmCropsChart";
